import React, { lazy, Suspense } from 'react';
import { Navigate, Route, Routes as Switch } from 'react-router-dom';
import Loading from 'components/Loading';

import AuthRoute from './AuthRoute';
import ProtectedRoute from './ProtectedRoute';
import SecurityRoute from './SecurityRoute';
import ExternalRoute from './ExternalRoute';
// ... (imports remain the same, I'll only replace the export default function)

const RootLayout = lazy(() => import('app/layout'));
const LoginPage = lazy(() => import('app/login/page'));
const DashboardPage = lazy(() => import('app/dashboard/page'));
const SettingPage = lazy(() => import('app/settings/page'));
const TagPage = lazy(() => import('app/master/product/tag/page'));
const SegmenPage = lazy(() => import('app/master/product/segmen/page'));
const RolePage = lazy(() => import('app/settings/role/page'));
const UpdateRolePage = lazy(() => import('app/settings/role/[id]/page'));
const RoleExternalPage = lazy(() => import('app/settings/role-external/page'));
const UpdateRoleExternalPage = lazy(() => import('app/settings/role-external/[id]/page'));
const AuctionPage = lazy(() => import('app/auction/auction-list/page'));
const AuctionDetailPage = lazy(() => import('app/auction/auction-list/[id]/page'));
const AuctionDetailUpdatePage = lazy(() => import('app/auction/auction-list/[id]/update/page'));
const AuctionDetailLotPage = lazy(() => import('app/auction/auction-list/[id]/lot/page'));
const AuctionDetailPricingPage = lazy(() => import('app/auction/auction-list/[id]/pricing/page'));
const AuctionDetailAllocationPage = lazy(() => import('app/auction/auction-list/[id]/allocation/[product_type_id]/page'));
const BannerPage = lazy(() => import('app/content/banner/page'));
const ContentBlogPage = lazy(() => import('app/content/blog/page'));
const ContentAboutPage = lazy(() => import('app/content/about/page'));
const ContentAboutDetailPage = lazy(() => import('app/content/about/[id]/page'));
const ContentAboutServicePage = lazy(() => import('app/content/about/service/[id]/page'));
const ContentFAQPage = lazy(() => import('app/content/faq/page'));
const ContentFAQDetailPage = lazy(() => import('app/content/faq/[id]/page'));
const ContentBlogDetailPage = lazy(() => import('app/content/blog/[id]/page'));
const ContentProcedurePage = lazy(() => import('app/content/procedure/page'));
const ContentProcedureDetailPage = lazy(() => import('app/content/procedure/[id]/[procedure_id]/page'));
const VehicleReleasePage = lazy(() => import('app/vehicle-release/page'));
const AcceptanceWebPage = lazy(() => import('app/acceptance/web/page'));
const AcceptanceWebAddPage = lazy(() => import('app/acceptance/web/add/page'));
const AcceptanceWebConfirmPage = lazy(() => import('app/acceptance/web/confirm/page'));
const AcceptancePartnerPage = lazy(() => import('app/acceptance/partner/page'));
const AcceptancePartnerDetailPage = lazy(() => import('app/acceptance/partner/[id]/page'));
const MobilizationPage = lazy(() => import('app/mobilization/page'));
const MobilizationConfirmPage = lazy(() => import('app/mobilization/confirm/[id]/page'));
const MobilizationDetailPage = lazy(() => import('app/mobilization/detail/[id]/page'));
const MobilizationDetailInquiryPage = lazy(() => import('app/mobilization/detail/[id]/inquiry/page'));
const ReviewDocumentPage = lazy(() => import('app/review-document/page'));
const ReviewDocumentDetailPage = lazy(() => import('app/review-document/[id]/page'));
const MasterProductTypePage = lazy(() => import('app/master/product/type/page'));
const MasterProductTypeDetailPage = lazy(() => import('app/master/product/type/[id]/page'));
const MasterProductTypeUpdatePage = lazy(() => import('app/master/product/type/[id]/update/page'));
const MasterInspectionAssessmentPage = lazy(() => import('app/master/inspection/assessment/page'));
const InventoryCarPage = lazy(() => import('app/inventory/page'));
const InventoryTypeAddPage = lazy(() => import('app/inventory/[type]/add/page'));
const InventoryDetailPage = lazy(() => import('app/inventory/[id]/page'));
const EditUnitInformationPage = lazy(() => import('app/inventory/[id]/unit/page'));
const EditDocumentPage = lazy(() => import('app/inventory/[id]/document/page'));
const ShowInspectionPage = lazy(() => import('app/inventory/[id]/inspection/page'));
const StatementAuctionPage = lazy(() => import('app/statement/auction/page'));
const StatementAuctionDetailPage = lazy(() => import('app/statement/auction/[id]/page'));
const StatementAuctionTransactionPage = lazy(() => import('app/statement/auction/transaction/[id]/page'));
const StatementMOUPage = lazy(() => import('app/statement/mou/page'));
const StatementSellerPaymentPage = lazy(() => import('app/statement/seller-payment/page'));
const StatementSellerPaymentDetailPage = lazy(() => import('app/statement/seller-payment/[id]/page'));
const StatementSellerDetailPage = lazy(() => import('app/statement/seller-payment/[id]/[seller_id]/page'));
const CustomerBasicPage = lazy(() => import('app/customer/basic/page'));
const CustomerPremiumPage = lazy(() => import('app/customer/premium/page'));
const CustomerBasicAddPage = lazy(() => import('app/customer/basic/add/page'));
const CustomerBasicUpdatePage = lazy(() => import('app/customer/basic/update/[id]/page'));
const CustomerDetailPage = lazy(() => import('app/customer/[id]/page'));
const RequestRefund = lazy(() => import('app/customer/request-refund/page'));
const MasterInspectionCategoryPage = lazy(() => import('app/master/inspection/category/page'));
const MasterInspectionPartPage = lazy(() => import('app/master/inspection/part/page'));
const DetailHistorybid = lazy(() => import('app/auction/auction-list/[id]/(tab)/history-bid/detail'));
const SentryTest = lazy(() => import('app/sentry-test/page'));
const AdminFee = lazy(() => import('app/auction/admin-fee/page'));
const AdminFeeAdd = lazy(() => import('app/auction/admin-fee/[id]/page'));
const AuctionFee = lazy(() => import('app/customer/auction-fee/page'));
const AuctionFeeAdd = lazy(() => import('app/customer/auction-fee/[id]/page'));
const VirtualAccountPage = lazy(() => import('app/customer/virtual-account/page'));
const VirtualAccountAddPage = lazy(() => import('app/customer/virtual-account/(tab)/va-bidder/add/page'));
const VirtualAccountUpdatePage = lazy(() => import('app/customer/virtual-account/(tab)/va-bidder/update/page'));
const VirtualAccountDetailPage = lazy(() => import('app/customer/virtual-account/[id]/page'));
const SellerListPage = lazy(() => import('app/customer/list-seller/page'));
const TransactionBidder = lazy(() => import('app/transaction/bidder/page'));
const TransactionBidderDetail = lazy(() => import('app/transaction/bidder/[id]/page'));
const TransactionDisbursement = lazy(() => import('app/transaction/disbursement/page'));
const TransactionDisbursementDetail = lazy(() => import('app/transaction/disbursement/[id]/page'));

// new feature
const BastSecurityPage = lazy(() => import('app/bast-security/page'));
const BastSecurityProfilePage = lazy(() => import('app/bast-security/profile/page'));
const BastInSecurityPage = lazy(() => import('app/bast-in-security/[id]/page'));
const BastOutSecurityPage = lazy(() => import('app/bast-out-security/[id]/page'));
const BastOutManagerPage = lazy(() => import('app/bast-out-manager/[id]/page'));
const HistoryBastPage = lazy(() => import('app/history-bast/page'));

const ReportAdiraMockupPage = lazy(() => import('app/report-adira/page'));
const ReportAdiraUnitAcceptance = lazy(() => import('app/report-adira/(tab)/penerimaan/page'));
const ReportAdiraAuctionReport = lazy(() => import('app/report-adira/(tab)/penjualan/page'));
const ReportAdiraStockChanges = lazy(() => import('app/report-adira/(tab)/mutasi/page'));
// end new feature

export default function RouteList() {
  return (
    <Suspense fallback={<Loading />}>
      <Switch>
        <Route>
          <Route element={<SentryTest />} path="page-sentry-tes" />
          <Route element={<AuthRoute />} path="login">
            <Route element={<LoginPage />} index />
          </Route>
          {/* Modul BAST Security — wajib login DAN hanya role "Security" yang boleh akses */}
          <Route element={<SecurityRoute />}>
            <Route element={<BastSecurityPage />} path="bast-security" />
            <Route element={<BastSecurityProfilePage />} path="bast-security/profile" />
            <Route element={<BastInSecurityPage />} path="bast-in-security/:id" />
            <Route element={<BastOutSecurityPage />} path="bast-out-security/:id" />
            <Route element={<HistoryBastPage />} path="history-bast" />
          </Route>
          {/* Mockup Report Adira — hanya user eksternal (is_external_flag true) yang boleh akses */}
          {/* <Route element={<ExternalRoute />}>
            <Route element={<ReportAdiraMockupPage />} path="report-seller" />
          </Route> */}
          <Route element={<ProtectedRoute />}>
            {/* Persetujuan TTD Manager BAST Out — link dibagikan manual ke manager.
                Cukup wajib login (tanpa scope menu khusus) dan tampil full screen
                di luar RootLayout karena bukan bagian dari sidebar admin. */}
            <Route element={<BastOutManagerPage />} path="bast-out-manager/:id" />
            <Route element={<AuctionDetailLotPage />} path="auction/:id/lot" />
            <Route element={<AuctionDetailPricingPage />} path="auction/:id/pricing" />
            <Route element={<AuctionDetailAllocationPage />} path="auction/:id/allocation/:product_type_id" />
            <Route element={<MasterProductTypeUpdatePage />} path="master/product/type/:id/update" />
            <Route element={<ContentAboutDetailPage />} path="content/about/:id" />
            <Route element={<RootLayout />}>
              <Route element={<Navigate replace to="/dashboard" />} index />
              <Route element={<DashboardPage />} path="dashboard" />
              <Route element={<ReviewDocumentPage />} path="review-document" />
              <Route element={<ReviewDocumentDetailPage />} path="review-document/:id" />
              <Route element={<SettingPage />} path="settings" />
              <Route element={<RolePage />} path="settings/role" />
              <Route element={<UpdateRolePage />} path="settings/role/:id" />
              <Route element={<RoleExternalPage />} path="settings/role-external" />
              <Route element={<UpdateRoleExternalPage />} path="settings/role-external/:id" />
              <Route element={<MasterProductTypePage />} path="master/product/type" />
              <Route element={<MasterProductTypeDetailPage />} path="master/product/type/:id" />
              <Route element={<MasterInspectionPartPage />} path="master/inspection/part" />
              <Route element={<MasterInspectionCategoryPage />} path="master/inspection/category" />
              <Route element={<MasterInspectionAssessmentPage />} path="master/inspection/assessment" />
              <Route element={<AuctionPage />} path="auction/list" />
              <Route element={<AuctionDetailPage />} path="auction/:id" />
              <Route element={<AuctionDetailUpdatePage />} path="auction/:id/update" />
              <Route element={<BannerPage />} path="content/banner" />
              <Route element={<ContentBlogPage />} path="content/blog" />
              <Route element={<ContentBlogDetailPage />} path="content/blog/:id" />
              <Route element={<ContentAboutPage />} path="content/about" />
              <Route element={<ContentAboutServicePage />} path="content/about/service/:id" />
              <Route element={<ContentFAQPage />} path="content/faq" />
              <Route element={<ContentFAQDetailPage />} path="content/faq/:id/:faq_id" />
              <Route element={<ContentProcedurePage />} path="content/procedure" />
              <Route element={<ContentProcedureDetailPage />} path="content/procedure/:id/:procedure_id" />
              <Route element={<VehicleReleasePage />} path="vehicle-release" />
              <Route element={<AcceptanceWebPage />} path="acceptance/web" />
              <Route element={<AcceptanceWebAddPage />} path="acceptance/web/add" />
              <Route element={<AcceptanceWebConfirmPage />} path="acceptance/web/:id/confirm" />
              <Route element={<MobilizationPage />} path="mobilization" />
              <Route element={<MobilizationConfirmPage />} path="mobilization/confirm/:id" />
              <Route element={<MobilizationDetailPage />} path="mobilization/detail/:id" />
              <Route element={<MobilizationDetailInquiryPage />} path="mobilization/detail/:id/inquiry" />
              <Route element={<AcceptancePartnerPage />} path="acceptance/partner" />
              <Route element={<AcceptancePartnerDetailPage />} path="acceptance/partner/:id" />
              <Route element={<InventoryCarPage />} path="inventory" />
              <Route element={<InventoryTypeAddPage />} path="inventory/:type/add" />
              <Route element={<InventoryDetailPage />} path="inventory/:id" />
              <Route element={<ShowInspectionPage />} path="inventory/:id/inspection" />
              <Route element={<EditUnitInformationPage />} path="inventory/:id/unit" />
              <Route element={<EditDocumentPage />} path="inventory/:id/document" />
              <Route element={<TagPage />} path="master/product/tag" />
              <Route element={<SegmenPage />} path="master/product/segmen" />
              <Route element={<StatementAuctionPage />} path="statement/auction" />
              <Route element={<StatementAuctionDetailPage />} path="statement/auction/:id" />
              <Route element={<StatementAuctionTransactionPage />} path="statement/auction/transaction/:id" />
              <Route element={<StatementMOUPage />} path="statement/mou" />
              <Route element={<StatementSellerPaymentPage />} path="statement/seller-payment/" />
              <Route element={<StatementSellerPaymentDetailPage />} path="statement/seller-payment/:id" />
              <Route element={<StatementSellerDetailPage />} path="statement/seller-payment/:id/:seller_id" />
              <Route element={<CustomerBasicPage />} path="customer/basic" />
              <Route element={<CustomerBasicAddPage />} path="customer/basic/add" />
              <Route element={<CustomerBasicUpdatePage />} path="customer/basic/update/:id" />
              <Route element={<CustomerPremiumPage />} path="customer/premium" />
              <Route element={<RequestRefund />} path="customer/request-refund" />
              <Route element={<VirtualAccountPage />} path="customer/virtual-account" />
              <Route element={<VirtualAccountAddPage />} path="customer/virtual-account/add-bidder" />
              <Route element={<VirtualAccountUpdatePage />} path="customer/virtual-account/update" />
              <Route element={<VirtualAccountDetailPage />} path="customer/virtual-account/:id" />
              <Route element={<SellerListPage />} path="customer/list-seller" />
              <Route element={<CustomerDetailPage />} path="customer/:id" />
              <Route element={<AdminFee />} path="auction/admin-fee" />
              <Route element={<AdminFeeAdd />} path="auction/admin-fee/:id" />
              <Route element={<DetailHistorybid />} path="auction/:auctionid/detail/:id" />
              <Route element={<TransactionBidder />} path="transaction/bidder" />
              <Route element={<TransactionBidderDetail />} path="transaction/bidder/:id" />
              <Route element={<TransactionDisbursement />} path="transaction/disbursement" />
              <Route element={<TransactionDisbursementDetail />} path="transaction/disbursement/:id" />
              <Route element={<AuctionFee />} path="customer/auction-fee" />
              <Route element={<AuctionFeeAdd />} path="customer/auction-fee/:id" />
              {/* Report Adira — hanya user eksternal (is_external_flag true). Guard-nya
                  di dalam RootLayout supaya sidebar & header tetap ikut tampil. */}
              <Route element={<ExternalRoute />}>
                <Route element={<ReportAdiraUnitAcceptance />} path="report-seller/unit-acceptance" />
                <Route element={<ReportAdiraAuctionReport />} path="report-seller/auction-report" />
                <Route element={<ReportAdiraStockChanges />} path="report-seller/stock-changes" />
              </Route>
              <Route element={<p>page not found</p>} path="*" />
            </Route>
          </Route>
        </Route>
      </Switch>
    </Suspense>
  );
}
